import { For, Show, createMemo, type Component } from 'solid-js';
import type { StrategyRun } from '~/domain/simulation';
import type { SessionEnd } from '~/domain/session';
import { BET_TYPE_LABELS } from '~/domain/bets';
import { formatCents } from '~/domain/profiles';
import { SESSION_END_LABELS } from '~/view/labels';

/** Whichever `SessionEnd` fired most often, so the row states its one dominant gate rather than the whole breakdown. */
function topGate(shares: Readonly<Record<SessionEnd, number>>): { end: SessionEnd; share: number } {
  let best: SessionEnd = 'bust';
  let bestShare = -1;
  for (const end of Object.keys(shares) as SessionEnd[]) {
    const share = shares[end];
    if (share > bestShare) {
      best = end;
      bestShare = share;
    }
  }
  return { end: best, share: Math.max(0, bestShare) };
}

/**
 * Colour for a genuinely signed figure — net position, not a magnitude like a
 * stake or a drawdown. Applying gain/loss colour to a value that is always
 * non-negative (a chip cost, a peak exposure) would read as "this cost is
 * good news", so the sign check only ever reaches figures where the sign is
 * the point.
 */
function signClass(cents: number): string {
  if (cents > 0) return 'text-gain';
  if (cents < 0) return 'text-loss';
  return 'text-ink';
}

/**
 * Colour for a p10–p90 spread: loss only when the whole band sits below
 * break-even, gain only when the whole band sits at or above it, and neutral
 * when the band straddles zero — a single colour on a range that crosses
 * break-even would claim a certainty the figure does not have.
 */
function netRangeClass(range: { readonly p10: number; readonly p90: number }): string {
  if (range.p90 <= 0) return 'text-loss';
  if (range.p10 >= 0) return 'text-gain';
  return 'text-ink';
}

export interface ComparisonTableProps {
  runs: readonly StrategyRun[];
  /** Display name per strategy id — the domain only carries ids. */
  nameOf: (strategyId: string) => string;
  selectedStrategyId: string | null;
  onSelect: (strategyId: string) => void;
  currency: string;
  class?: string;
}

const ComparisonTable: Component<ComparisonTableProps> = (props) => {
  const rows = createMemo(() => props.runs);

  return (
    <Show when={rows().length > 0}>
      <div class={`overflow-x-auto ${props.class ?? ''}`}>
        <table class="w-full min-w-[920px] border-collapse text-[11px]">
          <thead>
            <tr class="border-b border-line text-left text-[10px] uppercase tracking-wide text-ink-faint">
              <th class="pb-1 pr-2 font-medium">Strategy</th>
              <th class="pb-1 pr-2 text-right font-medium">Chip</th>
              <th class="pb-1 pr-2 text-right font-medium">Stake/spin</th>
              <th class="pb-1 pr-2 text-right font-medium">Covered</th>
              <th class="pb-1 pr-2 text-right font-medium">Ahead %</th>
              <th class="pb-1 pr-2 text-right font-medium">Median pocketed</th>
              <th class="pb-1 pr-2 text-right font-medium">Median net</th>
              <th class="pb-1 pr-2 text-right font-medium">p10–p90</th>
              <th class="pb-1 pr-2 text-right font-medium">Worst drawdown</th>
              <th class="pb-1 pr-2 text-right font-medium">Median spins</th>
              <th class="pb-1 text-left font-medium">Gate</th>
            </tr>
          </thead>
          <tbody>
            <For each={rows()}>
              {(run) => (
                <ComparisonRow
                  run={run}
                  name={props.nameOf(run.stats.strategyId)}
                  selected={run.stats.strategyId === props.selectedStrategyId}
                  onSelect={() => props.onSelect(run.stats.strategyId)}
                  currency={props.currency}
                />
              )}
            </For>
          </tbody>
        </table>
      </div>
    </Show>
  );
};

const ComparisonRow: Component<{
  run: StrategyRun;
  name: string;
  selected: boolean;
  onSelect: () => void;
  currency: string;
}> = (props) => {
  const stats = createMemo(() => props.run.stats);
  const money = (cents: number) => formatCents(cents, props.currency);
  const gate = createMemo(() => topGate(stats().endShares));

  const capped = createMemo(() => stats().capped.length > 0);
  const cappedTitle = createMemo(() =>
    stats()
      .capped.map(
        (entry) =>
          `${BET_TYPE_LABELS[entry.betType]} ${entry.target}: requested ${entry.requestedChips}, placed ${entry.placedChips} (${entry.reason})`,
      )
      .join('\n'),
  );

  return (
    <tr
      class="cursor-pointer border-b border-line/60 transition-colors last:border-0 hover:bg-surface-2"
      classList={{
        'bg-placed/15': props.selected,
        'opacity-60': stats().unplayable,
      }}
      onClick={props.onSelect}
    >
      <td class="py-1 pr-2 text-ink">
        <div class="flex items-center gap-1.5">
          <span class="truncate font-medium">{props.name}</span>
          <Show when={stats().unplayable}>
            <span
              class="shrink-0 rounded border border-loss/40 bg-loss/10 px-1 py-0.5 text-[9px] uppercase tracking-[0.06em] text-loss"
              title="Not one session could place this strategy at this table — every stake falls below its minimums. Nothing below describes anything."
            >
              unplayable
            </span>
          </Show>
          <Show when={!stats().unplayable && capped()}>
            <span
              class="shrink-0 rounded border border-loss/40 bg-loss/10 px-1 py-0.5 text-[9px] uppercase tracking-[0.06em] text-loss"
              title={`Capped to this table's limits — a strategy nobody could actually place as drawn:\n${cappedTitle()}`}
            >
              capped
            </span>
          </Show>
        </div>
        {/* §9.1: the edge is fixed by the table's odds, never by how the
            outcome distribution below happened to land — so it sits right
            beside the strategy it describes rather than in a shared footer
            a reader could lose track of. */}
        <div class="tnum mt-0.5 text-[10px] text-ink-faint">
          Theoretical edge {(stats().theoreticalEdge * 100).toFixed(2)}% — fixed by the table's
          odds, unchanged by how this strategy stacks its bets.
        </div>
      </td>
      <td class="tnum py-1 pr-2 text-right text-ink">{money(stats().denominationCents)}</td>
      <td class="tnum py-1 pr-2 text-right text-ink">{money(stats().stakeCentsPerSpin)}</td>
      <td class="tnum py-1 pr-2 text-right text-ink-dim">{stats().pocketsCovered}</td>
      <td
        class="tnum py-1 pr-2 text-right text-ink"
        title={`${(stats().sessionAheadProbability * 100).toFixed(1)}% of individual sessions finished ahead`}
      >
        {(stats().campaignAheadProbability * 100).toFixed(1)}%
      </td>
      <td class="tnum py-1 pr-2 text-right text-ink">{money(stats().sessionPocketedCents.median)}</td>
      <td class={`tnum py-1 pr-2 text-right ${signClass(stats().campaignNetCents.median)}`}>
        {money(stats().campaignNetCents.median)}
      </td>
      <td class={`tnum py-1 pr-2 text-right ${netRangeClass(stats().campaignNetCents)}`}>
        {money(stats().campaignNetCents.p10)} to {money(stats().campaignNetCents.p90)}
      </td>
      <td class="tnum py-1 pr-2 text-right text-ink-dim">
        {money(stats().worstPeakOutOfPocketCents)}
      </td>
      <td class="tnum py-1 pr-2 text-right text-ink-dim">{stats().spinsToGate.median}</td>
      <td class="py-1 text-left text-ink-dim">
        {SESSION_END_LABELS[gate().end]} <span class="tnum">{(gate().share * 100).toFixed(0)}%</span>
      </td>
    </tr>
  );
};

export default ComparisonTable;
